import type { RatingBoard } from '@brush/api-client';
import { describeApiError } from '@brush/api-client';
import { ErrorNotice, ErrorRetry, Pill, Spinner } from '@brush/ui';
import { Shield, TrendingUp } from 'lucide-react';
import { useState } from 'react';
import { useCreateRatingBoard, usePointsSystems, useRatingBoards } from '../../queries';
import { pointsSystemLine } from '../../ratings/builder';
import { useRoom } from '../../room';
import { useUnsaved } from '../../unsaved';
import { RatingBoardBuilder } from './RatingBoardBuilder';
import { RatingBoardStandings } from './RatingBoardStandings';

/**
 * The room's rating boards, and the platform templates it can start one from — §9.1's Ratings
 * section, which is **parallel to Leaderboards and never inside it**: they answer different
 * questions, and nesting one implies a rating is a view of a board, which is the misconception §1
 * exists to prevent.
 *
 * **Two ways into a board** (§9.2, Phase 6): read what it produces, or configure it. They are
 * separate screens rather than tabs on one, because they answer separate questions and only one of
 * them holds unsaved work — the standings screen is where the lifecycle lives (Start, Close,
 * Recalculate, the cadence), and the builder is where the five documents are edited. §9.9 drew the
 * same line for a leaderboard: a field saves with the form, a transition acts at once.
 */
export function RatingBoardsView() {
  const { room, roomId } = useRoom();
  const boardsQ = useRatingBoards(roomId);
  const create = useCreateRatingBoard(roomId);

  /*
   * An id and a mode, never the board — the shape `LeaderboardsView` settled on. A board held from
   * when the screen opened is a snapshot, so a change made elsewhere would leave the dialog reading
   * stale fields under a title that looks current; and it is what lets a board deleted in another
   * tab close this screen rather than save into a 404.
   */
  const [viewing, setViewing] = useState<{ id: string; mode: 'standings' | 'edit' } | null>(null);
  const { leave } = useUnsaved();

  // The room's points system catalogue, for the card line. A CLIENT-SIDE JOIN rather than a
  // `PointsSystemName` on the DTO, and the honest degradation is as much the reason as the cost:
  // this query returns exactly the systems this room may select, so a lookup that misses IS the
  // answer "you can no longer see this" — where a server-resolved name would be reported regardless
  // and leave the card naming a system that is absent from the builder's own picker. It is also
  // already paid for: §9.2's section 2 needs the same list.
  const systems = usePointsSystems(roomId, false);

  if (boardsQ.isLoading) return <Spinner label="Loading ratings…" />;

  if (boardsQ.isError || !room) {
    return (
      <ErrorRetry message="Couldn't load ratings." onRetry={() => void boardsQ.refetch()} />
    );
  }

  const all = boardsQ.data ?? [];
  const templates = all.filter((b) => b.roomId === null);
  const boards = all.filter((b) => b.roomId !== null);

  // Re-resolved against the live list on every render rather than held as an object, the shape
  // `LeaderboardsView` settled on: a board held from when the screen opened is a snapshot, so a
  // change made elsewhere would leave this reading stale fields under a title that looks current.
  const open = viewing ? boards.find((b) => b.id === viewing.id) ?? null : null;

  if (viewing && open) {
    return viewing.mode === 'edit' ? (
      <RatingBoardBuilder board={open} roomId={room.id} onBack={() => setViewing(null)} />
    ) : (
      <RatingBoardStandings board={open} onBack={() => setViewing(null)} />
    );
  }

  return (
    <div>
      <ErrorNotice message={describeApiError(create.error)} className="mb-4" />

      {boards.length === 0 ? (
        /*
         * NOT AN ONBOARDING STATE, and it stopped being one at Phase 4b. Every room is created
         * with its default rating (locked rule 3, §2.6) and nothing can delete it, so a room with
         * no boards at all is a room whose default is missing — which the product has no way to
         * produce. The copy therefore diagnoses rather than welcomes: "no ratings yet" over a
         * room that is supposed to have one would send an operator looking for a Create button
         * that does not exist, and Phase 6's builder is not it either.
         *
         * Kept rather than deleted because the alternative is a blank page for the one case that
         * most needs explaining, and because a database somebody has been editing by hand is
         * exactly where this would show up.
         */
        <div className="rounded-xl border border-dashed border-ink-800/20 bg-white p-10 text-center">
          <TrendingUp className="mx-auto mb-3 size-10 text-ink-800/20" />
          <h3 className="font-display font-semibold text-ink-950">
            This room&rsquo;s default rating is missing
          </h3>
          <p className="mx-auto mt-1 max-w-lg text-sm text-ink-800/60">
            Every room is created with an <strong>All Tournaments</strong> rating that cannot be
            deleted, so this should not be possible. Nothing is lost &mdash; no results, standings
            or players are affected &mdash; but the rating itself needs putting back.
          </p>
          <p className="mx-auto mt-2 max-w-lg text-xs text-ink-800/50">
            Ask a platform administrator to look at it. You can still start a rating of your own
            from a preset below in the meantime.
          </p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {boards.map((board) => (
            <BoardCard
              key={board.id}
              board={board}
              systems={systems.data ?? null}
              onOpen={() => setViewing({ id: board.id, mode: 'standings' })}
              // Through `leave` like every other navigation in this app: opening a builder from a
              // screen that holds no draft cannot lose anything today, and routing it any other way
              // is how the one exit somebody forgets gets added later.
              onEdit={() => leave(() => setViewing({ id: board.id, mode: 'edit' }))}
            />
          ))}
        </div>
      )}

      {templates.length > 0 ? (
        <section className="mt-8">
          <h3 className="font-display font-semibold text-ink-950">Platform presets</h3>
          <p className="mt-1 max-w-2xl text-sm text-ink-800/60">
            Defined by the platform and shared by every room. Using one copies its settings onto a
            rating of your own, which you can then adjust — the preset itself never changes and
            never scores anything.
          </p>

          <div className="mt-4 grid gap-4 md:grid-cols-2">
            {templates.map((template) => (
              <article
                key={template.id}
                className="rounded-xl border border-ink-800/10 bg-white p-5 shadow-sm"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h4 className="font-display font-semibold text-ink-950">{template.name}</h4>
                      <span className="inline-flex items-center gap-1 rounded bg-gold-500/15 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-ink-900">
                        <Shield className="size-2.5" /> Platform
                      </span>
                    </div>
                    {template.description ? (
                      <p className="mt-1 text-xs leading-relaxed text-ink-800/60">
                        {template.description}
                      </p>
                    ) : null}
                  </div>
                </div>

                <button
                  type="button"
                  disabled={create.isPending}
                  onClick={() =>
                    create.mutate({
                      roomId: room.id,
                      name: template.name,
                      description: template.description,
                      clonedFromBoardId: template.id,

                      /*
                       * The preset's own points system, carried across — §5.1 and §5.2 are a pair
                       * and the board is meaningless without the system: pinned to a flat
                       * placement table it scores a win at 100 whether the field was 40 or 4,000.
                       *
                       * Read off the template rather than matched by name here, which is what
                       * keeps the reference's name in one place. It remains the room's choice
                       * (§2.2): the new board is a Draft, where §2.4 leaves the selection freely
                       * changeable, and it becomes a pin only when somebody starts it.
                       */
                      pointsSystemId: template.pointsSystemId,
                      pointsSystemVersion: template.pointsSystemVersionNumber,

                      /*
                       * THE FIVE BLOCKS ARE PASSED THROUGH AS THE OPAQUE DOCUMENTS THEY ARE.
                       *
                       * <b>The reason has changed and is worth stating, because the old one was a
                       * hazard rather than a preference.</b> Until the Phase 6 mirror move, `scope`
                       * could not go through `readScope`/`toScopeDocument` at all: that mirror
                       * typed both bounds as non-optional `string` and coerced a null to `''`,
                       * which `points-scope.schema.json` refuses against its `yyyy-mm-dd` pattern —
                       * so a round trip turned this preset's deliberately open bounds into a 400
                       * and an operator saw a platform preset that could not be used. `PointsScope`
                       * now carries `string | null` on both and the round trip is byte-stable
                       * (`packages/ui/src/points/scope.test.js` gates exactly this document).
                       *
                       * They still travel opaquely, for the ordinary reason: a clone copies what
                       * the template holds, and parsing five documents to hand back the same five
                       * is a chance for this build's shape to drop a key it does not know about.
                       */
                      scope: template.scope,
                      cohort: template.cohort,
                      window: template.window,
                      aggregation: template.aggregation,
                      qualification: template.qualification,

                      // Private until an operator says otherwise — and a Draft board reads as
                      // Private regardless, so this is what it will be when they start it rather
                      // than what it is now.
                      visibility: 'private',
                      recalcMode: template.recalcMode,
                      recalcCron: template.recalcCron,
                    })
                  }
                  className="mt-4 w-full rounded-lg bg-felt-700 py-2 text-sm font-medium text-white hover:bg-felt-900 disabled:opacity-50"
                >
                  {create.isPending ? 'Adding…' : 'Use in this room'}
                </button>
              </article>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}

function BoardCard({
  board,
  systems,
  onOpen,
  onEdit,
}: {
  board: RatingBoard;
  /** The room's catalogue, or null while it is still loading — the two are told apart. */
  systems: readonly { id: string; name: string; isPlatform: boolean }[] | null;
  onOpen: () => void;
  onEdit: () => void;
}) {
  return (
    <article className="rounded-xl border border-ink-800/10 bg-white p-5 shadow-sm">
      <div className="mb-2 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-display font-semibold text-ink-950">{board.name}</h3>
            <Pill
              tone={
                board.status === 'running' ? 'good' : board.status === 'closed' ? 'neutral' : 'warn'
              }
            >
              {board.status === 'running' ? 'Running' : board.status === 'closed' ? 'Closed' : 'Draft'}
            </Pill>
          </div>
          {board.description ? (
            <p className="mt-1 text-xs leading-relaxed text-ink-800/60">{board.description}</p>
          ) : null}
        </div>
        <TrendingUp className="size-5 shrink-0 text-felt-500" />
      </div>

      {/*
        THE NEEDS-SETUP STATE, and it is a fix rather than a feature.

        This read "Scoring with points system version 1", and both halves were wrong for the state
        they described. Present tense, so a Draft board — which scores nothing and has published
        nothing — read as one that is running; and a bare version number, so the operator was never
        told WHAT it would score with. ADR 0011 Decision 12 pre-selects §5.1's GPI-style system
        precisely so a new room has something to start from, and that promise was invisible on the
        one screen that mentioned it. The §2.4 distinction the old comment drew correctly — a Draft
        board's version is a selection and a started one's is the pin — is still drawn, and now the
        TENSE carries it too. `pointsSystemLine` holds the wording and its tests.
      */}
      <p className="mb-4 text-[11px] leading-relaxed text-ink-800/60">
        {pointsSystemLine(board, systems)}
      </p>

      <div className="flex gap-2">
        <button
          type="button"
          onClick={onOpen}
          className="flex-1 rounded-lg bg-felt-700 py-2 text-sm font-medium text-white hover:bg-felt-900"
        >
          View rating
        </button>
        <button
          type="button"
          onClick={onEdit}
          className="rounded-lg border border-ink-800/20 px-3 py-2 text-sm text-ink-800/70 hover:bg-ink-800/5"
        >
          Configure
        </button>
      </div>
    </article>
  );
}
